import type { FC } from "react";
import { Suspense, lazy, useEffect } from "react";

import type { ISavedLogoTileWithCounter } from "@/src/brand/saved-brands/SavedBrands";
import { useUserAuthenticationContext } from "@/src/contexts";
import PageLayout from "@/src/shared/page-layout";
import type { ILink } from "@Components/Link";
import { getFromUrl, jumpToSectionId } from "@Components/Modules/Helpers";
import { FollowedBrands } from "@Routes/BrandLinks";

import type { IContentContainer } from "../common/ContentContainer";
import ContentContainer from "../common/ContentContainer";
import type { ILocationInfo } from "../location/LocationInfo";
import { LocationInfo } from "../location/LocationInfo";
import type { IBrandImageBanner } from "./BrandImageBanner";
import { BrandImageBanner, BrandImageBannerAppearance } from "./BrandImageBanner";
import type { IBrandsList } from "./BrandsList";
import { BrandsList } from "./BrandsList";
import type { ISuggestedBrands } from "./SuggestedBrands";
import { SuggestedBrands } from "./SuggestedBrands";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./FollowedBrandsTab.module.css";

const SavedBrands = lazy(() => import("./saved-brands/SavedBrands"));

export interface IFollowedBrandsTab {
    imageBanner: IBrandImageBanner;
    suggestedBrands: ISuggestedBrands;
    followedBrandsTitle: string;
    findBrandsTitle: string;
    allBrands: ILink;
    brandsList: IBrandsList;
    contentContainers?: IContentContainer;
    locationInfo: ILocationInfo;
    initialData: IFollowedBrandsTabResponse;
}

interface IFollowedBrandsTabResponse {
    brands: ISavedLogoTileWithCounter[];
    newsletterActive: boolean;
}

const FollowedBrandsTab: FC<IFollowedBrandsTab> = (props) => {
    const onClick = (_ev: React.MouseEvent<HTMLAnchorElement>, elemId: string | null): void => {
        if (!elemId) {
            return;
        }

        jumpToSectionId(elemId);
    };
    const { isAuthenticated } = useUserAuthenticationContext();

    const savedBrandsClick = (ev: React.MouseEvent<HTMLAnchorElement>) =>
        onClick(ev, props.suggestedBrands.clientId);

    const suggestedBrandsClick = (ev: React.MouseEvent<HTMLAnchorElement>) =>
        onClick(ev, props.brandsList.clientId);

    useEffect(() => {
        const queryResult = getFromUrl("position");
        let timeout = 0;
        if (
            queryResult &&
            (queryResult === props.suggestedBrands.clientId || queryResult === props.brandsList.clientId)
        ) {
            timeout = window.setTimeout(() => jumpToSectionId(queryResult, true), 800);
        }

        return () => clearTimeout(timeout);
    }, []);

    return (
        <PageLayout
            layoutAdUnitPath="marki"
            slugTarget={FollowedBrands}
            breadcrumbs={[{ labelKey: "followedBrands" }]}
        >
            <div className={css.followedBrandsTabComponent}>
                <BrandImageBanner
                    {...props.imageBanner}
                    onClick={savedBrandsClick}
                    appearance={BrandImageBannerAppearance.ImageDescription}
                />
                <div className={cssPortal.standardContainer}>
                    <div className={css.locationContainer}>
                        <LocationInfo {...props.locationInfo} />
                    </div>
                    {isAuthenticated && (
                        <Suspense>
                            <SavedBrands />
                        </Suspense>
                    )}
                    <SuggestedBrands {...props.suggestedBrands} onButtonClick={suggestedBrandsClick} />
                    <BrandsList {...props.brandsList} />
                    <ContentContainer {...props.contentContainers} />
                </div>
            </div>
        </PageLayout>
    );
};

export { FollowedBrandsTab };
